import type { StateCreator } from "../store";
import type { AppStore } from "../index";
import type { TabsPaths, ShareExtensionPayload, Message, JwtPayload } from "../types";
import { parseJwtPayload as coreParseJwtPayload, isInitialized as isSwitchCoreInitialized } from "@/core_bridge/module_api";
import { stateServices } from "@/state/module_api";

export const EMPTY_JWT_PAYLOAD: JwtPayload = Object.freeze({});

let cachedJwtToken: string | undefined;
let cachedJwtCoreInitialized: boolean | undefined;
let cachedJwtPayload: JwtPayload = EMPTY_JWT_PAYLOAD;

const getSavedToken = (key: string): string | undefined => {
  if (typeof localStorage === "undefined") return undefined;
  try {
    const val = localStorage.getItem(key);
    return val || undefined;
  } catch {
    return undefined;
  }
};

export interface AuthSlice {
  // Core state
  apiToken: string | undefined;
  callToken: string | undefined;
  communityToken: string | undefined;
  jitsiToken: string | undefined;
  activeTab: TabsPaths;
  isRootPageInit: boolean;
  myNumber: string | undefined;
  myProfilePhoto: string | undefined;
  isSearchable: boolean | undefined;
  iosPushToken: string | undefined;
  shareExtensionPayload: ShareExtensionPayload | undefined;
  hasCodeBeenSet: boolean;
  nativeProcessedMessages: Message[];
  profileRenderRef: number;
  usersTabShowSearch: boolean;
  threadsTabShowCreateGroup: boolean;

  // Actions
  setApiToken: (token: string | undefined) => void;
  setCallToken: (token: string | undefined) => void;
  setCommunityToken: (token: string | undefined) => void;
  setJitsiToken: (token: string | undefined) => void;
  setActiveTab: (tab: TabsPaths) => void;
  setIsRootPageInit: (value: boolean) => void;
  setMyNumber: (number: string | undefined) => void;
  setMyProfilePhoto: (photo: string | undefined) => void;
  setIsSearchable: (value: boolean | undefined) => void;
  setIosPushToken: (token: string | undefined) => void;
  setShareExtensionPayload: (payload: ShareExtensionPayload | undefined) => void;
  setHasCodeBeenSet: (value: boolean) => void;
  addNativeProcessedMessage: (message: Message) => void;
  clearNativeProcessedMessages: () => void;
  incrementProfileRenderRef: () => void;
  setUsersTabShowSearch: (value: boolean) => void;
  setThreadsTabShowCreateGroup: (value: boolean) => void;
  logout: () => void;
}

/**
 * Fallback JWT parser using native JavaScript
 * Used when WASM module isn't ready yet
 */
function parseJwtFallback(token: string): JwtPayload {
  try {
    if (!token) return {};
    const parts = token.split(".");
    if (parts.length !== 3) return {};
    // Handle base64url encoding (replace - with +, _ with /)
    let base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    // Add padding if needed
    const padding = base64.length % 4;
    if (padding) {
      base64 += "=".repeat(4 - padding);
    }
    const decoded = atob(base64);
    return JSON.parse(decoded);
  } catch {
    return {};
  }
}

/**
 * Parse JWT payload using switch-core's centralized implementation
 * Routes through Rust for proper base64url handling and padding
 * Falls back to native JS parsing when WASM isn't ready
 */
export function parseJwt(token: string): JwtPayload {
  const coreInitialized = isSwitchCoreInitialized();
  if (token === cachedJwtToken && coreInitialized === cachedJwtCoreInitialized) {
    return cachedJwtPayload;
  }

  // Use fallback when WASM isn't initialized yet
  const payload = coreInitialized
    ? (coreParseJwtPayload(token) ?? EMPTY_JWT_PAYLOAD)
    : parseJwtFallback(token);

  cachedJwtToken = token;
  cachedJwtCoreInitialized = coreInitialized;
  cachedJwtPayload = payload;
  return payload;
}

/**
 * Get initial token lazily to avoid accessing stateServices before initialization.
 * The store is created at module load time, but stateServices may not be initialized yet.
 */
const getInitialToken = (): string | undefined => {
  try {
    return stateServices.sessionState.getToken() || undefined;
  } catch {
    // State layer not initialized yet - will be hydrated later
    return undefined;
  }
};

export const createAuthSlice: StateCreator<AppStore, unknown, unknown, AuthSlice> = (
  set
) => ({
  // Initial state
  apiToken: getInitialToken(),
  callToken: undefined,
  communityToken: getSavedToken("communityToken"),
  jitsiToken: undefined,
  activeTab: "/threads-tab",
  isRootPageInit: false,
  myNumber: undefined,
  myProfilePhoto: undefined,
  isSearchable: undefined,
  iosPushToken: undefined,
  shareExtensionPayload: undefined,
  hasCodeBeenSet: false,
  nativeProcessedMessages: [],
  profileRenderRef: 0,
  usersTabShowSearch: false,
  threadsTabShowCreateGroup: false,

  // Actions
  setApiToken: (token) =>
    set((state) => {
      state.apiToken = token;
      void stateServices.sessionState.setToken(token || null);
      // Send token to service worker for authenticated push notification fetches
      // Using dynamic import to avoid circular dependency
      import("@/app/module_api").then(({ syncTokenToServiceWorker }) => {
        syncTokenToServiceWorker(token, {});
      });
    }),

  setCallToken: (token) =>
    set((state) => {
      state.callToken = token;
    }),

  setCommunityToken: (token) =>
    set((state) => {
      state.communityToken = token;
      if (typeof localStorage !== "undefined") {
        try {
          if (token) localStorage.setItem("communityToken", token);
          else localStorage.removeItem("communityToken");
        } catch {
          // ignore storage errors
        }
      }
    }),

  setJitsiToken: (token) =>
    set((state) => {
      state.jitsiToken = token;
    }),

  setActiveTab: (tab) =>
    set((state) => {
      state.activeTab = tab;
    }),

  setIsRootPageInit: (value) =>
    set((state) => {
      state.isRootPageInit = value;
    }),

  setMyNumber: (number) =>
    set((state) => {
      state.myNumber = number;
    }),

  setMyProfilePhoto: (photo) =>
    set((state) => {
      state.myProfilePhoto = photo;
    }),

  setIsSearchable: (value) =>
    set((state) => {
      state.isSearchable = value;
    }),

  setIosPushToken: (token) =>
    set((state) => {
      state.iosPushToken = token;
    }),

  setShareExtensionPayload: (payload) =>
    set((state) => {
      state.shareExtensionPayload = payload;
    }),

  setHasCodeBeenSet: (value) =>
    set((state) => {
      state.hasCodeBeenSet = value;
    }),

  addNativeProcessedMessage: (message) =>
    set((state) => {
      state.nativeProcessedMessages.push(message);
    }),

  clearNativeProcessedMessages: () =>
    set((state) => {
      state.nativeProcessedMessages = [];
    }),

  incrementProfileRenderRef: () =>
    set((state) => {
      state.profileRenderRef += 1;
    }),

  setUsersTabShowSearch: (value) =>
    set((state) => {
      state.usersTabShowSearch = value;
    }),

  setThreadsTabShowCreateGroup: (value) =>
    set((state) => {
      state.threadsTabShowCreateGroup = value;
    }),

  logout: () =>
    set((state) => {
      // Clear all auth-related state
      state.apiToken = undefined;
      state.callToken = undefined;
      state.communityToken = undefined;
      state.jitsiToken = undefined;
      state.myNumber = undefined;
      state.myProfilePhoto = undefined;
      state.isSearchable = undefined;
      state.iosPushToken = undefined;
      state.shareExtensionPayload = undefined;
      state.hasCodeBeenSet = false;
      state.nativeProcessedMessages = [];
      state.isRootPageInit = false;
      void stateServices.sessionState.clearToken();

      // Clear localStorage items (since we're not going through setters)
      if (typeof localStorage !== "undefined") {
        try {
          localStorage.removeItem("apiToken");
          localStorage.removeItem("communityToken");
          localStorage.removeItem("displayPhotoUrl");
          localStorage.removeItem("displayPhotoKey");
          localStorage.removeItem("account_mnemonic");
          localStorage.removeItem("auth_keypair");
          localStorage.removeItem("balance");
          localStorage.removeItem("llm-messages");
        } catch {
          // ignore storage errors
        }
      }
    }),
});
